package ro.horatiu_udrea.resursedeblocat

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowLeft
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.stringResource
import resursedeblocat.composeapp.generated.resources.*

@Composable
fun TrackQuestionnaireScreen(
    viewModel: TrackQuestionnaireViewModel,
    onBack: () -> Unit,
    onFinished: () -> Unit
) {
    val scrollState = rememberScrollState()
    val totalSteps = trackingSteps
    
    val progress by animateFloatAsState(
        targetValue = (viewModel.currentStep + 1).toFloat() / totalSteps.toFloat()
    )

    LaunchedEffect(viewModel.currentStep) {
        when (viewModel.currentStep) {
            0 -> viewModel.ensureDefaultAnswer(theoryResourcesKey, theoryResourcesDefault)
            1 -> viewModel.ensureDefaultAnswer(practicalActionsKey, practicalActionsDefault)
            2 -> viewModel.ensureDefaultAnswer(workHoursKey, workHoursDefault)
            3 -> {
                viewModel.ensureDefaultAnswer(workEnergyKey, energyDefault)
                viewModel.ensureDefaultAnswer(environmentEnergyKey, energyDefault)
            }
            4 -> viewModel.ensureDefaultAnswer(weeklyIncomeKey, weeklyIncomeDefault)
            5 -> viewModel.ensureDefaultAnswer(wellBeingKey, wellBeingDefault)
            6 -> viewModel.ensureDefaultAnswer(deblocatMomentKey, deblocatMomentDefault)
            7 -> viewModel.ensureDefaultAnswer(notWellBeingKey, notWellBeingDefault)
            8 -> viewModel.ensureDefaultAnswer(participatedMinigroupKey, participatedMinigroupDefault)
            9 -> viewModel.ensureDefaultAnswer(interactionMinigroupKey, interactionMinigroupDefault)
            10 -> viewModel.ensureDefaultAnswer(joinMinigroupKey, joinMinigroupDefault)
            11 -> viewModel.ensureDefaultAnswer(anythingElseKey, anythingElseDefault)
        }
    }

    val currentMonth = viewModel.tracking.month
    val nextMonth = if (currentMonth == 12) 1 else currentMonth + 1
    val nextMonthRes = when (nextMonth) {
        1 -> Res.string.month_1
        2 -> Res.string.month_2
        3 -> Res.string.month_3
        4 -> Res.string.month_4
        5 -> Res.string.month_5
        6 -> Res.string.month_6
        7 -> Res.string.month_7
        8 -> Res.string.month_8
        9 -> Res.string.month_9
        10 -> Res.string.month_10
        11 -> Res.string.month_11
        12 -> Res.string.month_12
        else -> Res.string.month_1
    }

    val monthRes = when (viewModel.tracking.month) {
        1 -> Res.string.month_1
        2 -> Res.string.month_2
        3 -> Res.string.month_3
        4 -> Res.string.month_4
        5 -> Res.string.month_5
        6 -> Res.string.month_6
        7 -> Res.string.month_7
        8 -> Res.string.month_8
        9 -> Res.string.month_9
        10 -> Res.string.month_10
        11 -> Res.string.month_11
        12 -> Res.string.month_12
        else -> Res.string.month_1
    }

    BaseScreenLayout(
        scrollState = scrollState,
        footer = {
            Row(
                modifier = Modifier.fillMaxWidth().padding(vertical = 16.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                TextButton(
                    onClick = { 
                        if (viewModel.currentStep > 0) {
                            viewModel.previousStep()
                        } else {
                            onBack()
                        }
                    },
                    enabled = true
                ) {
                    Icon(Icons.AutoMirrored.Filled.KeyboardArrowLeft, contentDescription = null)
                    Spacer(modifier = Modifier.width(4.dp))
                    Text(stringResource(Res.string.track_back))
                }
                
                if (viewModel.isLoading) {
                    CircularProgressIndicator(modifier = Modifier.size(24.dp))
                }
                
                Button(
                    onClick = { viewModel.nextStep(onFinished) },
                    shape = MaterialTheme.shapes.medium
                ) {
                    val isLast = viewModel.currentStep == totalSteps - 1
                    Text(stringResource(if (isLast) Res.string.track_finish else Res.string.track_next))
                    if (!isLast) {
                        Spacer(modifier = Modifier.width(4.dp))
                        Icon(Icons.AutoMirrored.Filled.KeyboardArrowRight, contentDescription = null)
                    }
                }
            }
        }
    ) {
        Column(modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp)) {
            LinearProgressIndicator(
                progress = { progress },
                modifier = Modifier.fillMaxWidth().height(8.dp).clip(RoundedCornerShape(4.dp)),
                color = MaterialTheme.colorScheme.primary,
                trackColor = MaterialTheme.colorScheme.primary.copy(alpha = 0.2f)
            )
            
            Spacer(modifier = Modifier.height(16.dp))
            
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                IconButton(onClick = onBack) {
                    Icon(Icons.Default.Close, contentDescription = null)
                }
                Text(
                    text = stringResource(Res.string.monthly_tracking_title, stringResource(monthRes), viewModel.tracking.year),
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.weight(1f)
                )
            }
        }

        Column(
            modifier = Modifier.fillMaxWidth().padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(24.dp)
        ) {
            when (viewModel.currentStep) {
                0 -> ListInputScreen(
                    title = stringResource(Res.string.tracking_theory_title),
                    subtitle = stringResource(Res.string.tracking_theory_subtitle),
                    examples = listOf(
                        stringResource(Res.string.tracking_theory_example1),
                        stringResource(Res.string.tracking_theory_example2),
                        stringResource(Res.string.tracking_theory_example3),
                        stringResource(Res.string.tracking_theory_example4)
                    ),
                    items = viewModel.tracking.answers[theoryResourcesKey] as? List<String> ?: theoryResourcesDefault,
                    onItemsChange = { viewModel.setAnswer(theoryResourcesKey, it) }
                )
                1 -> ListInputScreen(
                    title = stringResource(Res.string.tracking_practical_title),
                    subtitle = stringResource(Res.string.tracking_practical_subtitle),
                    examples = listOf(
                        stringResource(Res.string.tracking_practical_example1),
                        stringResource(Res.string.tracking_practical_example2),
                        stringResource(Res.string.tracking_practical_example3)
                    ),
                    items = viewModel.tracking.answers[practicalActionsKey] as? List<String> ?: practicalActionsDefault,
                    onItemsChange = { viewModel.setAnswer(practicalActionsKey, it) }
                )
                2 -> SliderInputScreen(
                    title = stringResource(Res.string.tracking_work_hours_title),
                    subtitle = stringResource(Res.string.tracking_work_hours_subtitle),
                    value = (viewModel.tracking.answers[workHoursKey] as? Int ?: workHoursDefault).toFloat(),
                    onValueChange = { viewModel.setAnswer(workHoursKey, it.toInt()) },
                    range = 0f..80f,
                    steps = 81
                )
                3 -> DualSliderInputScreen(
                    title = stringResource(Res.string.tracking_energy_title),
                    subtitle = stringResource(Res.string.tracking_energy_subtitle),
                    label1 = stringResource(Res.string.tracking_energy_work_label),
                    value1 = (viewModel.tracking.answers[workEnergyKey] as? Int ?: energyDefault).toFloat(),
                    onValueChange1 = { viewModel.setAnswer(workEnergyKey, it.toInt()) },
                    label2 = stringResource(Res.string.tracking_energy_env_label),
                    value2 = (viewModel.tracking.answers[environmentEnergyKey] as? Int ?: energyDefault).toFloat(),
                    onValueChange2 = { viewModel.setAnswer(environmentEnergyKey, it.toInt()) }
                )
                4 -> NumberInputScreen(
                    title = stringResource(Res.string.tracking_weekly_income_title),
                    subtitle = stringResource(Res.string.tracking_weekly_income_subtitle),
                    value = viewModel.tracking.answers[weeklyIncomeKey] as? Int ?: weeklyIncomeDefault,
                    onValueChange = { viewModel.setAnswer(weeklyIncomeKey, it) },
                    unit = "lei"
                )
                5 -> TextAreaInputScreen(
                    title = stringResource(Res.string.tracking_well_being_title),
                    subtitle = stringResource(Res.string.tracking_well_being_subtitle),
                    value = viewModel.tracking.answers[wellBeingKey] as? String ?: wellBeingDefault,
                    onValueChange = { viewModel.setAnswer(wellBeingKey, it) }
                )
                6 -> TextAreaInputScreen(
                    title = stringResource(Res.string.tracking_deblocat_title),
                    subtitle = stringResource(Res.string.tracking_deblocat_subtitle),
                    value = viewModel.tracking.answers[deblocatMomentKey] as? String ?: deblocatMomentDefault,
                    onValueChange = { viewModel.setAnswer(deblocatMomentKey, it) }
                )
                7 -> TextAreaInputScreen(
                    title = stringResource(Res.string.tracking_not_well_being_title),
                    subtitle = stringResource(Res.string.tracking_not_well_being_subtitle),
                    value = viewModel.tracking.answers[notWellBeingKey] as? String ?: notWellBeingDefault,
                    onValueChange = { viewModel.setAnswer(notWellBeingKey, it) }
                )
                8 -> SelectionInputScreen(
                    title = stringResource(Res.string.tracking_participated_minigroup_title),
                    subtitle = stringResource(Res.string.tracking_participated_minigroup_subtitle),
                    value = viewModel.tracking.answers[participatedMinigroupKey] as? Boolean ?: participatedMinigroupDefault,
                    onValueChange = { viewModel.setAnswer(participatedMinigroupKey, it) }
                )
                9 -> TextAreaInputScreen(
                    title = stringResource(Res.string.tracking_interaction_minigroup_title),
                    subtitle = stringResource(Res.string.tracking_interaction_minigroup_subtitle),
                    value = viewModel.tracking.answers[interactionMinigroupKey] as? String ?: interactionMinigroupDefault,
                    onValueChange = { viewModel.setAnswer(interactionMinigroupKey, it) }
                )
                10 -> SelectionInputScreen(
                    title = stringResource(Res.string.tracking_join_minigroup_title, stringResource(nextMonthRes)),
                    subtitle = stringResource(Res.string.tracking_join_minigroup_subtitle),
                    value = viewModel.tracking.answers[joinMinigroupKey] as? Boolean ?: joinMinigroupDefault,
                    onValueChange = { viewModel.setAnswer(joinMinigroupKey, it) }
                )
                11 -> TextAreaInputScreen(
                    title = stringResource(Res.string.tracking_anything_else_title),
                    subtitle = stringResource(Res.string.tracking_anything_else_subtitle),
                    value = viewModel.tracking.answers[anythingElseKey] as? String ?: anythingElseDefault,
                    onValueChange = { viewModel.setAnswer(anythingElseKey, it) }
                )
            }
        }
        
        viewModel.errorMessage?.let { error ->
            Text(
                text = error,
                color = MaterialTheme.colorScheme.error,
                modifier = Modifier.padding(16.dp)
            )
        }
    }
}

@Composable
fun ListInputScreen(
    title: String,
    subtitle: String,
    examples: List<String>,
    items: List<String>,
    onItemsChange: (List<String>) -> Unit
) {
    val displayItems = remember(items) {
        if (items.isEmpty()) listOf("", "", "") else items
    }

    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        Text(text = title, style = MaterialTheme.typography.headlineSmall, fontWeight = FontWeight.Bold)
        Text(text = subtitle, style = MaterialTheme.typography.bodyLarge)
        
        Column(modifier = Modifier.padding(start = 8.dp)) {
            Text(text = stringResource(Res.string.tracking_example_label), style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.SemiBold)
            examples.forEach { example ->
                Text(text = "• $example", style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
            }
        }

        displayItems.forEachIndexed { index, item ->
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                OutlinedTextField(
                    value = item,
                    onValueChange = { newValue ->
                        val newList = displayItems.toMutableList()
                        newList[index] = newValue
                        onItemsChange(newList)
                    },
                    modifier = Modifier.weight(1f),
                    shape = MaterialTheme.shapes.medium,
                    placeholder = { Text(stringResource(Res.string.tracking_add_entry_placeholder)) }
                )
                IconButton(
                    onClick = {
                        val newList = displayItems.toMutableList()
                        newList.removeAt(index)
                        if (newList.isEmpty()) newList.add("")
                        onItemsChange(newList)
                    }
                ) {
                    Icon(
                        Icons.Default.Delete, 
                        contentDescription = stringResource(Res.string.tracking_delete_content_description), 
                        tint = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
        }

        Button(
            onClick = {
                val newList = displayItems.toMutableList()
                newList.add("")
                onItemsChange(newList)
            },
            modifier = Modifier.align(Alignment.CenterHorizontally)
        ) {
            Icon(Icons.Default.Add, contentDescription = null)
            Spacer(modifier = Modifier.width(4.dp))
            Text(stringResource(Res.string.tracking_add_more))
        }
    }
}

@Composable
fun SliderInputScreen(
    title: String,
    subtitle: String,
    hint: String = "",
    value: Float,
    onValueChange: (Float) -> Unit,
    range: ClosedFloatingPointRange<Float>,
    steps: Int
) {
    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        Text(text = title, style = MaterialTheme.typography.headlineSmall, fontWeight = FontWeight.Bold)
        Text(text = subtitle, style = MaterialTheme.typography.bodyLarge)
        if (hint.isNotEmpty()) {
            Text(text = hint, style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant, fontStyle = FontStyle.Italic)
        }
        
        Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.fillMaxWidth()) {
            Slider(
                value = value,
                onValueChange = onValueChange,
                valueRange = range,
                steps = steps - 1,
                colors = SliderDefaults.colors(
                    thumbColor = MaterialTheme.colorScheme.primary,
                    activeTrackColor = MaterialTheme.colorScheme.primary,
                    inactiveTrackColor = MaterialTheme.colorScheme.background,
                    activeTickColor = MaterialTheme.colorScheme.background
                )
            )
            Text(
                text = value.toInt().toString(),
                style = MaterialTheme.typography.displayMedium,
                color = MaterialTheme.colorScheme.primary,
                fontWeight = FontWeight.Bold
            )
        }
    }
}

@Composable
fun DualSliderInputScreen(
    title: String,
    subtitle: String,
    hint: String = "",
    label1: String,
    value1: Float,
    onValueChange1: (Float) -> Unit,
    label2: String,
    value2: Float,
    onValueChange2: (Float) -> Unit
) {
    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        Text(text = title, style = MaterialTheme.typography.headlineSmall, fontWeight = FontWeight.Bold)
        Text(text = subtitle, style = MaterialTheme.typography.bodyLarge)
        if (hint.isNotEmpty()) {
            Text(text = hint, style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant, fontStyle = FontStyle.Italic)
        }
        
        Spacer(modifier = Modifier.height(8.dp))
        
        Text(text = label1, style = MaterialTheme.typography.titleMedium)
        Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.fillMaxWidth()) {
            Slider(
                value = value1,
                onValueChange = onValueChange1,
                valueRange = 1f..10f,
                steps = 8,
                colors = SliderDefaults.colors(
                    thumbColor = MaterialTheme.colorScheme.primary,
                    activeTrackColor = MaterialTheme.colorScheme.primary,
                    inactiveTrackColor = MaterialTheme.colorScheme.background,
                    activeTickColor = MaterialTheme.colorScheme.background
                )
            )
            Text(
                text = value1.toInt().toString(),
                style = MaterialTheme.typography.titleLarge,
                color = MaterialTheme.colorScheme.primary,
                fontWeight = FontWeight.Bold
            )
        }

        Spacer(modifier = Modifier.height(16.dp))

        Text(text = label2, style = MaterialTheme.typography.titleMedium)
        Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.fillMaxWidth()) {
            Slider(
                value = value2,
                onValueChange = onValueChange2,
                valueRange = 1f..10f,
                steps = 8,
                colors = SliderDefaults.colors(
                    thumbColor = MaterialTheme.colorScheme.primary,
                    activeTrackColor = MaterialTheme.colorScheme.primary,
                    inactiveTrackColor = MaterialTheme.colorScheme.background,
                    activeTickColor = MaterialTheme.colorScheme.background
                )
            )
            Text(
                text = value2.toInt().toString(),
                style = MaterialTheme.typography.titleLarge,
                color = MaterialTheme.colorScheme.primary,
                fontWeight = FontWeight.Bold
            )
        }
    }
}

@Composable
fun NumberInputScreen(
    title: String,
    subtitle: String,
    hint: String = "",
    value: Int,
    onValueChange: (Int) -> Unit,
    unit: String
) {
    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        Text(text = title, style = MaterialTheme.typography.headlineSmall, fontWeight = FontWeight.Bold)
        Text(text = subtitle, style = MaterialTheme.typography.bodyLarge)
        if (hint.isNotEmpty()) {
            Text(text = hint, style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant, fontStyle = FontStyle.Italic)
        }
        
        OutlinedTextField(
            value = if (value == 0) "" else value.toString(),
            onValueChange = { newValue ->
                if (newValue.isEmpty()) {
                    onValueChange(0)
                } else if (newValue.all { it.isDigit() }) {
                    onValueChange(newValue.toIntOrNull() ?: 0)
                }
            },
            modifier = Modifier.width(150.dp),
            suffix = { Text(unit) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            singleLine = true,
            shape = RoundedCornerShape(12.dp)
        )
    }
}

@Composable
fun SelectionInputScreen(
    title: String,
    subtitle: String,
    value: Boolean,
    onValueChange: (Boolean) -> Unit
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Text(
            text = title,
            style = MaterialTheme.typography.headlineSmall,
            fontWeight = FontWeight.Bold
        )
        if (subtitle.isNotEmpty()) {
            Text(
                text = subtitle,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
        
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Button(
                onClick = { onValueChange(true) },
                modifier = Modifier.weight(1f),
                colors = ButtonDefaults.buttonColors(
                    containerColor = if (value) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.surfaceVariant,
                    contentColor = if (value) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onSurfaceVariant
                ),
                shape = MaterialTheme.shapes.medium
            ) {
                Text(stringResource(Res.string.tracking_yes))
            }
            
            Button(
                onClick = { onValueChange(false) },
                modifier = Modifier.weight(1f),
                colors = ButtonDefaults.buttonColors(
                    containerColor = if (!value) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.surfaceVariant,
                    contentColor = if (!value) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onSurfaceVariant
                ),
                shape = MaterialTheme.shapes.medium
            ) {
                Text(stringResource(Res.string.tracking_no))
            }
        }
    }
}

@Composable
fun TextAreaInputScreen(
    title: String,
    subtitle: String,
    hint: String = "",
    value: String,
    onValueChange: (String) -> Unit
) {
    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        Text(text = title, style = MaterialTheme.typography.headlineSmall, fontWeight = FontWeight.Bold)
        Text(text = subtitle, style = MaterialTheme.typography.bodyLarge)
        if (hint.isNotEmpty()) {
            Text(text = hint, style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
        
        OutlinedTextField(
            value = value,
            onValueChange = onValueChange,
            modifier = Modifier.fillMaxWidth(),
            minLines = 5,
            shape = MaterialTheme.shapes.medium,
            placeholder = { Text(stringResource(Res.string.tracking_answer_placeholder)) }
        )
    }
}
